import { useNavigate } from 'react-router';
import {
  TopNav,
  Card,
  Grid,
  Stack,
  Skeleton,
  EmptyState,
  Callout,
  Icon,
  icons,
  theme,
} from '@agentisai/design-system';
import type { UsageData } from '@agentisai/shared-types';
import { UserMenu } from '../components/UserMenu';
import { useUsage } from '../queries/useUsage';
import { formatCreditsValue } from '../lib/formatCredits';

/** "1,234" — thousands-grouped, no decimals (token/turn/call counts). */
function formatCount(n: number): string {
  return n.toLocaleString('en-AU');
}

/** `costAud`/`credits` are absent (never `"0"`) when unpriced (D13-03's null
 * discipline, ADR-0063). Render that explicitly — a zero here would read as
 * "free", a materially different and wrong claim. */
function formatCredits(credits: string | undefined): string {
  return credits === undefined ? 'Not yet priced' : formatCreditsValue(credits);
}

interface UsageShareCardProps {
  title: string;
  share: UsageData['you'] | UsageData['org'];
  memberCount?: number;
}

function UsageShareCard({ title, share, memberCount }: UsageShareCardProps) {
  const totalTokens =
    share.tokens.inputTokens +
    share.tokens.outputTokens +
    share.tokens.cacheReadTokens +
    share.tokens.cacheWriteTokens;

  return (
    // A labelled <section> is a `region` landmark, so the card is reachable by role
    // + name (E2E R3) and screen-reader users can jump between "you" and "org".
    <Card as="section" aria-label={title} elevation={1}>
      <Card.Header>
        <Card.Title>{title}</Card.Title>
        {memberCount !== undefined && (
          <Card.Meta>
            {memberCount} member{memberCount === 1 ? '' : 's'}
          </Card.Meta>
        )}
      </Card.Header>
      <Card.Content>
        <Stack direction="column" gap={2}>
          <Stat label="Turns" value={formatCount(share.turns)} />
          <Stat label="Model calls" value={formatCount(share.modelCalls)} />
          <Stat label="Tokens" value={formatCount(totalTokens)} />
          <Stat label="Credits" value={formatCredits(share.credits)} />
        </Stack>
      </Card.Content>
    </Card>
  );
}
UsageShareCard.displayName = 'UsageShareCard';

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justify="between" align="baseline">
      <span
        style={{ color: theme.color.neutral.faint, fontSize: theme.typography.fontSize.bodySm }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: theme.typography.fontSize.bodyLg,
          fontWeight: theme.typography.fontWeight.semibold,
        }}
      >
        {value}
      </span>
    </Stack>
  );
}
Stat.displayName = 'Stat';

/**
 * ProfilePage (D13-07) — the Usage view the `UserMenu` `Profile` item now
 * points at. Shows this period's consumption for the signed-in user and
 * their organisation off the D13-05 `GET /api/v1/workstreams/usage` endpoint.
 *
 * Per the D13-07 spec: no other user's individual usage ever appears (the
 * endpoint itself never returns it); an absent `costAud`/`credits` renders
 * "not yet priced", never a fake `0`/`$0.00`; a zero-turn user is a normal
 * empty state, not an error.
 */
export function ProfilePage() {
  const navigate = useNavigate();
  const query = useUsage();

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: theme.color.neutral.bgSurface,
      }}
    >
      <TopNav onBrandClick={() => void navigate('/')} trailing={<UserMenu />} />
      <main style={{ padding: theme.space[6], maxWidth: 960, margin: '0 auto', width: '100%' }}>
        <h1
          style={{
            margin: 0,
            marginBottom: theme.space[5],
            fontSize: theme.typography.fontSize.h2,
            fontWeight: theme.typography.fontWeight.semibold,
          }}
        >
          Usage
        </h1>

        {query.isLoading && (
          <Grid columns={{ xs: 1, md: 2 }} gap={4}>
            <Skeleton shape="card" height={220} />
            <Skeleton shape="card" height={220} />
          </Grid>
        )}

        {query.isError && (
          <div role="alert">
            <Callout tone="danger">Couldn't load usage. We'll retry automatically.</Callout>
          </div>
        )}

        {query.isSuccess &&
          (query.data.data.you.turns === 0 && query.data.data.org.turns === 0 ? (
            <EmptyState
              icon={<Icon as={icons.Zap} size="lg" />}
              title="No usage yet this period"
              description="Once you or your organisation run a turn, this period's consumption shows up here."
            />
          ) : (
            <Stack direction="column" gap={5}>
              <Grid columns={{ xs: 1, md: 2 }} gap={4}>
                <UsageShareCard title="Your usage this period" share={query.data.data.you} />
                <UsageShareCard
                  title="Your organisation this period"
                  share={query.data.data.org}
                  memberCount={query.data.data.org.memberCount}
                />
              </Grid>
              <Callout tone="tip">
                Credits are calibrated against a provisional rate and may be re-based as pricing is
                finalised.
              </Callout>
            </Stack>
          ))}
      </main>
    </div>
  );
}
ProfilePage.displayName = 'ProfilePage';
